// Ultra-light SWR-style cache. Show cached data INSTANTLY on mount,
// revalidate in background. Eliminates the "blank spinner on every nav" feel.
import { useEffect, useState } from "react";
import api from "@/lib/api";

const _cache = new Map(); // key: { data, ts }
const _pending = new Map(); // key: Promise
const _subs = new Map(); // key: Set<setState>
const DEFAULT_TTL = 60_000; // 60 s stale-while-revalidate window

async function _fetch(key, url) {
  if (_pending.has(key)) return _pending.get(key);
  const p = api.get(url).then(({ data }) => {
    _cache.set(key, { data, ts: Date.now() });
    (_subs.get(key) || []).forEach((fn) => fn(data));
    return data;
  }).catch(() => _cache.get(key)?.data ?? null)
    .finally(() => _pending.delete(key));
  _pending.set(key, p);
  return p;
}

export function invalidate(key) { _cache.delete(key); }

export function useSharedData(key, url, ttl = DEFAULT_TTL) {
  const cached = _cache.get(key);
  const [data, setData] = useState(cached?.data ?? null);
  useEffect(() => {
    if (!_subs.has(key)) _subs.set(key, new Set());
    _subs.get(key).add(setData);
    const stale = !cached || Date.now() - cached.ts > ttl;
    if (stale) _fetch(key, url); else if (data !== cached.data) setData(cached.data);
    return () => _subs.get(key)?.delete(setData);
  }, [key, url]); // eslint-disable-line
  return { data, loading: data === null };
}
